import { useCallback, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import cc from "classcat";
import { useLocalStorage } from "usehooks-ts";
import Account from "byzantine/src/Account";
import { modules } from "byzantine";

import AddAccountModal from "../../../../../components/transfer/AddAccountModal";
import { SudoProvider } from "../../../../../components/SudoContext";
import SudoDialog from "../../../../../components/SudoDialog";
import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import { VerifyAccountDialog } from "../../../../../components/settings/linked_accounts/ExternalAccountManager";
import {
  DASHBOARD_LINKED_ACCOUNTS_BASENAME,
  VERIFYROUTES,
  useIsAddAccountsDialogOpen,
} from "../../../../../components/settings/linked_accounts/hooks";
import ExternalAccountRow from "../ExternalAccountRow/ExternalAccountRow";
import { useRefreshStaleExternalAccounts } from "../hooks/useRefreshExternalAccounts";
import { useExternalAccountsStorage } from "../storage";
import { createFNVHashSync } from "../../../../../components/authentication/utils";
import ExternalAccountsTopRow from "../ExternalAccountsTopRow/ExternalAccountsTopRow";
import SectionCard from "../../../../../components/SectionCard";

import { DAYS_UNTIL_DISPLAYED_AGAIN } from "./constants";
import styles from "./ExternalAccountsContainer.module.scss";
import { mergeAccountsById } from "./utils";

import type { AddAccountMode } from "../../../../../components/settings/linked_accounts/types";

const ExternalAccountsContainer = ({
  externalAccounts,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo,
}: {
  externalAccounts: Account[];
  uatMicrodepAmntOne?: string;
  uatMicrodepAmntTwo?: string;
}) => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const hasExternalAccountCreatePermission =
    modules.externalAccounts.useAddExternalAccountPermission();
  const userId = currentUser?.id?.toString() || "";
  const dismissedStorageKey = `externalAccountsSectionDismissedUntil_${createFNVHashSync(
    userId
  )}`;
  const [dismissed, setDismissed] = useLocalStorage<string | undefined>(
    dismissedStorageKey,
    undefined
  );
  const hasLinkedExternalAccounts = externalAccounts.length > 0;
  const isDismissedRecently = Boolean(
    dismissed && new Date(dismissed).getTime() > Date.now()
  );
  const [latestExternalAccounts, setLatestExternalAccounts] =
    useState(externalAccounts);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  const [isVerifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [addAccountMode, setAddAccountMode] =
    useState<AddAccountMode>("default");
  const isAddAccountsDialogOpen = useIsAddAccountsDialogOpen(
    DASHBOARD_LINKED_ACCOUNTS_BASENAME
  );
  const [isContainerHidden, setIsContainerHidden] = useState(
    !hasLinkedExternalAccounts && isDismissedRecently
  );

  const [{ sortedBy, hiddenHashedAccountIds }] =
    useExternalAccountsStorage(userId);

  const hiddenHashedAccountIdsSet = useMemo(
    () => new Set(hiddenHashedAccountIds),
    [hiddenHashedAccountIds]
  );

  useRefreshStaleExternalAccounts({
    externalAccounts,
    onRefresh: setLatestExternalAccounts,
  });

  const sortedAccounts = useMemo(() => {
    const accountsCopy = [...latestExternalAccounts];
    if (sortedBy === "alphabetically") {
      return accountsCopy.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    }

    return accountsCopy.sort(
      (a, b) => b.availableBalanceAsFloat() - a.availableBalanceAsFloat()
    );
  }, [latestExternalAccounts, sortedBy]);

  const closeContainer = useCallback(() => {
    const date = new Date();
    date.setDate(date.getDate() + DAYS_UNTIL_DISPLAYED_AGAIN);

    setDismissed(date.toString());
    setIsContainerHidden(true);
  }, [setDismissed]);

  const openVerifyDialog = useCallback((account: Account) => {
    setSelectedAccount(account);
    setVerifyDialogOpen(true);
  }, []);

  const handleVerifiedAccountUpdate = useCallback((updatedAccount: Account) => {
    setLatestExternalAccounts((previousAccounts) =>
      previousAccounts.map((account) =>
        updatedAccount.id === account.id ? updatedAccount : account
      )
    );
  }, []);

  const handleOpenAddAccountDialog = useCallback(() => {
    setAddAccountMode("default");
    navigate(
      `/${DASHBOARD_LINKED_ACCOUNTS_BASENAME}/${VERIFYROUTES.verifyOptions}`
    );
  }, [navigate]);

  const updateAccountsOnVerify = (updatedAccounts: API.ExternalAccount[]) => {
    const deserializedAccounts = updatedAccounts.map((account) =>
      Account.deserialize(account)
    );

    setLatestExternalAccounts((accounts: Account[]) =>
      mergeAccountsById(accounts, deserializedAccounts)
    );
  };

  const handleOpenInstantVerificationDialog = useCallback(
    (mode: AddAccountMode) => {
      setAddAccountMode(mode);
      navigate(
        `/${DASHBOARD_LINKED_ACCOUNTS_BASENAME}/${VERIFYROUTES.verifyOptions}`
      );
    },
    [navigate]
  );

  const handleCloseAddAccountDialog = useCallback(() => {
    setAddAccountMode("default");
    navigate("/dashboard");
  }, [navigate]);

  // When the user has no linked external accounts and lacks the permission to
  // add one, there is nothing actionable to show, so hide the entire section.
  if (!hasLinkedExternalAccounts && !hasExternalAccountCreatePermission) {
    return null;
  }

  return (
    <SectionCard
      classNames={cc([
        styles.cardWrapper,
        isContainerHidden && styles.cardWrapperHidden,
      ])}
      paddingSize={null}
      testId="external-accounts-card-wrapper"
    >
      <div
        className={cc([
          styles.container,
          isContainerHidden && styles.containerHidden,
        ])}
        data-testid="external-accounts-container"
        aria-hidden={isContainerHidden}
      >
        <div
          className={cc([
            styles.content,
            (externalAccounts.length === 0 ||
              hiddenHashedAccountIdsSet.size === sortedAccounts.length) &&
              styles.contentEmpty,
          ])}
        >
          <ExternalAccountsTopRow
            hiddenHashedAccountIdsSet={hiddenHashedAccountIdsSet}
            externalAccounts={latestExternalAccounts}
            onLinkAccount={handleOpenAddAccountDialog}
            onClose={closeContainer}
          />
          {sortedAccounts.map((account) => (
            <div
              key={account.id}
              data-testid={`external-account-row-wrapper-${account.id}`}
              className={cc([
                styles.cardRow,
                hiddenHashedAccountIdsSet.has(createFNVHashSync(account.id)) &&
                  styles.isHidden,
              ])}
            >
              <ExternalAccountRow
                account={account}
                onVerify={openVerifyDialog}
                onTriggerInstantVerification={
                  handleOpenInstantVerificationDialog
                }
              />
            </div>
          ))}
        </div>
      </div>

      <VerifyAccountDialog
        account={selectedAccount}
        isDialogOpen={isVerifyDialogOpen}
        setDialogOpen={setVerifyDialogOpen}
        callback={handleVerifiedAccountUpdate}
        uatMicrodepAmntOne={uatMicrodepAmntOne}
        uatMicrodepAmntTwo={uatMicrodepAmntTwo}
      />

      <SudoProvider basename={DASHBOARD_LINKED_ACCOUNTS_BASENAME}>
        <AddAccountModal
          open={isAddAccountsDialogOpen}
          handleClose={handleCloseAddAccountDialog}
          updateAccounts={updateAccountsOnVerify}
          basename={DASHBOARD_LINKED_ACCOUNTS_BASENAME}
          addAccountMode={addAccountMode}
        />
        <SudoDialog />
      </SudoProvider>
    </SectionCard>
  );
};

export default ExternalAccountsContainer;
